import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useIntl } from "react-intl";
import { useQuery } from "@tanstack/react-query";
import { getPublicUiCustomizationByGuid } from "../config/axios/apis/ui-customization/ui-customization";
import { buildSeededDefaults } from "../utils/branding/uiConfig.slots";
import { fromConfiguration } from "../utils/branding/uiConfig.mappers";
import { TApplicationUIConfigForm } from "../utils/branding/uiConfig.types";
import { BrandingMode, resolveBrandingMode } from "../utils/branding/brandingTheme";
import { useSessionStorage } from "./useSessionStorage";
import { BRANDING_ID_PARAM, BRANDING_MODE_PARAM } from "../utils/publicPageParams";

export const BRANDING_QUERY_KEY = "branding-config";

// Build a query string carrying only the branding params (brandingId, mode) present in `source`. Used for
// client-side jumps between the public pages (e.g. Login → Register) so branding persists — those nav
// events never pass through InitAuth's stash, so the params must ride along in the URL themselves.
export const buildBrandingSearch = (source: URLSearchParams): string => {
    const out = new URLSearchParams();
    [BRANDING_ID_PARAM, BRANDING_MODE_PARAM].forEach((key) => {
        const value = source.get(key);
        if (value) {
            out.set(key, value);
        }
    });
    return out.toString();
};

// Append the current branding params to a route so client-side jumps between public pages (Login ↔ Register)
// keep the branded look. Returns the bare path when there are no branding params to carry.
export const withBrandingParams = (path: string, source: URLSearchParams): string => {
    const search = buildBrandingSearch(source);
    return search ? `${path}?${search}` : path;
};
// Transient hand-off of the branding params across the auth redirect chain. InitAuth stashes them here on
// the way out (the bounce to the BE strips the query string); useBrandingConfig reads them once on the
// return to /login, writes them back into the URL, and clears them. sessionStorage survives the full-page
// bounce (same origin, same tab); the values are consumed immediately so the URL stays the single source of
// truth. `mode` rides along with the id so an explicit ?mode= choice survives the round-trip too — without
// it the page would revert to the config's defaultMode and silently drop the visitor's selection.
// The stash is also cleared on successful auth (see Authorized.tsx) so it can't linger for the tab and
// resurface branding on a later bare /login when the return never lands back on the public login form.
export const BRANDING_ID_STORAGE_KEY = "branding-preview-id";
export const BRANDING_MODE_STORAGE_KEY = "branding-preview-mode";

export interface UseBrandingConfigResult {
    /** The `brandingId` GUID from the URL, or null when absent. */
    brandingId: string | null;
    /** True only while a branding config referenced by a present `brandingId` is being fetched. */
    isLoading: boolean;
    /** True only when a config was found AND it is enabled — the single "apply branding" switch. */
    isEnabled: boolean;
    /** The hydrated config when enabled; null otherwise (absent / 404 / disabled → render defaults). */
    config: TApplicationUIConfigForm | null;
    /** Resolved light/dark mode (only meaningful when enabled). */
    mode: BrandingMode;
}

// Reads the `?brandingId=` (and optional `?mode=`) URL params and fetches that app's branding config
// from the public endpoint. Fully backward-compatible: no param, unknown GUID (404), or a config with
// `isEnabled: false` all collapse to `{ isEnabled: false, config: null }` so callers render defaults.
// `skip` fully disables the URL read + fetch — used by the NG-8456 live preview, which drives the
// provider from an unsaved draft and must never hit the network even if the editor URL carries params.
const useBrandingConfig = ({ skip = false }: { skip?: boolean } = {}): UseBrandingConfigResult => {
    const [params, setSearchParams] = useSearchParams();
    const urlBrandingId = skip ? null : params.get(BRANDING_ID_PARAM);
    const urlMode = params.get(BRANDING_MODE_PARAM);
    const theme = useTheme();
    const { formatMessage } = useIntl();

    // The auth flow (login → init-auth → BE authorize → back to /login) strips the query string, so InitAuth
    // stashes the branding params on the way out. When we land back without the param, restore them into the
    // URL (which becomes the single source of truth again) and consume the stash — from here, deleting the
    // param restores the default look. Preview mode never touches the stash.
    const { value: storedBrandingId, clearStorage: clearStoredBrandingId } = useSessionStorage<string | null>(BRANDING_ID_STORAGE_KEY, null);
    const { value: storedMode, clearStorage: clearStoredMode } = useSessionStorage<string | null>(BRANDING_MODE_STORAGE_KEY, null);
    useEffect(() => {
        if (!skip && !urlBrandingId && storedBrandingId) {
            clearStoredBrandingId();
            if (storedMode) {
                clearStoredMode();
            }
            setSearchParams(
                (prev) => {
                    prev.set(BRANDING_ID_PARAM, storedBrandingId);
                    // Restore the explicit mode alongside the id when one was stashed, so ?mode= survives
                    // the round-trip instead of collapsing to the config defaultMode.
                    if (storedMode) {
                        prev.set(BRANDING_MODE_PARAM, storedMode);
                    }
                    return prev;
                },
                { replace: true },
            );
        }
    }, [skip, urlBrandingId, storedBrandingId, storedMode, clearStoredBrandingId, clearStoredMode, setSearchParams]);

    // Render from the stash immediately (no flash) while the effect above writes it back into the URL.
    const brandingId = skip ? null : (urlBrandingId ?? storedBrandingId ?? null);
    // Same immediate-render treatment for the mode, so the branded page shows the stashed mode on the
    // return hop rather than flashing the config default before the effect rewrites the URL.
    const effectiveMode = urlMode ?? (skip ? null : storedMode);

    const { data, isLoading } = useQuery({
        queryKey: [BRANDING_QUERY_KEY, brandingId],
        // Coerce the absent case to null (never undefined) — the public read resolves undefined on a
        // 404/unknown GUID, but React Query requires a non-undefined queryFn result.
        queryFn: async ({ signal }) => (await getPublicUiCustomizationByGuid(brandingId as string, { signal })) ?? null,
        enabled: !!brandingId,
        retry: false,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    return useMemo(() => {
        // The public endpoint may return the full record or the bare config blob — handle both.
        const record = data?.data as unknown as (Record<string, unknown> & { configuration?: unknown }) | undefined;
        const rawConfiguration = record?.configuration ?? record;
        const seeded = buildSeededDefaults(theme, formatMessage);
        const config = brandingId && rawConfiguration ? fromConfiguration(rawConfiguration, seeded) : null;
        const isEnabled = !!config && config.isEnabled;

        return {
            brandingId,
            isLoading: !!brandingId && isLoading,
            isEnabled,
            config: isEnabled ? config : null,
            mode: resolveBrandingMode(effectiveMode, config?.defaultMode),
        };
    }, [brandingId, effectiveMode, data, isLoading, theme, formatMessage]);
};

export default useBrandingConfig;
